/**
 * Architecture H — classe, objectif, et la fiche est là.
 *
 * Le parcours tient en une phrase : on choisit une classe, on choisit un
 * objectif concret, et une fiche cohérente APPARAÎT. Les ajustements sont
 * possibles, jamais obligatoires. Le téléchargement ne demande pas de compte ;
 * le compte est proposé APRÈS, pour conserver et retrouver.
 *
 * Ce qui a disparu par rapport au parcours actuel, et volontairement :
 * les propositions à valider, le bouton « Tout ajouter », la fiche d'abord
 * vide, le choix du nombre d'exercices AVANT d'avoir vu quoi que ce soit, et
 * les codes de générateurs affichés en lecture ordinaire.
 *
 * Ce qui reste, parce que ce sont de vrais besoins : d'autres nombres,
 * remplacer une forme, voir le corrigé, réordonner, retirer (avec annulation),
 * allonger, et vérifier le papier — dans une vue séparée, jamais dans une A4
 * miniature qui servirait d'éditeur.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, BookmarkPlus, Check, ChevronDown, ChevronUp, Download, Eye,
  FileText, Loader2, Pencil, RefreshCw, Repeat, Trash2, Undo2,
} from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import MathHtmlRenderer from "../../lib/MathHtmlRenderer";
import { useToast } from "../../hooks/use-toast";
import { useLogin } from "../../contexts/LoginContext";
import {
  noterActionApresConnexion, reprendreActionApresConnexion,
} from "../../lib/actionApresConnexion";
import { useAuth } from "../../hooks/useAuth";
import ApercuPapier from "./ApercuPapier";
import { API_FICHES, corpsExport, entetesFiche, useFicheH } from "./useFicheH";
import { resumerCharge } from "./charge";
import ChoixObjectif from "./ChoixObjectif";
import BugReportDialog from "../admin/BugReportDialog";
import SignalerLaPage from "../SignalerLaPage";
import "./fiche-h.css";


/* ------------------------------------------------------------------ */
/* 1 · CHOISIR — une classe, puis un objectif concret                  */
/* ------------------------------------------------------------------ */


/* ------------------------------------------------------------------ */
/* 2 · LIRE ET AJUSTER — des blocs à la taille de l'écran              */
/* ------------------------------------------------------------------ */

function ExerciceBloc({
  exercice, index, total, formes, occupe,
  onRenouveler, onRemplacer, onMonter, onDescendre, onRetirer,
  onSignaler,
}) {
  const [corrigeOuvert, setCorrigeOuvert] = useState(false);

  return (
    <article className="rounded-lg border border-border bg-card">
      <header className="flex items-center gap-2 border-b border-border px-3 py-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
          {index + 1}
        </span>
        {/* La FORME, en clair. Jamais la clé du générateur ni la graine :
            l'audit demande de retirer ce bruit de la lecture ordinaire. */}
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {exercice.forme_libelle}
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" aria-label={`Autres actions pour l'exercice ${index + 1}`}>
              ⋯
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="h-exercise-menu max-w-[calc(100vw-32px)]">
            <DropdownMenuItem disabled={index === 0} onClick={() => onMonter(index)}>
              <ChevronUp className="mr-2 h-4 w-4" /> Monter
            </DropdownMenuItem>
            <DropdownMenuItem disabled={index === total - 1} onClick={() => onDescendre(index)}>
              <ChevronDown className="mr-2 h-4 w-4" /> Descendre
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {formes.filter((f) => f.libelle !== exercice.forme_libelle).map((f) => (
              <DropdownMenuItem key={f.libelle} onClick={() => onRemplacer(index, f.libelle)}>
                <Repeat className="mr-2 h-4 w-4" /> Remplacer par « {f.libelle} »
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            {onSignaler && <DropdownMenuItem onSelect={() => onSignaler(exercice)}>Signaler un problème</DropdownMenuItem>}
            <DropdownMenuItem disabled={total === 1} onClick={() => onRetirer(index)} className="text-destructive">
              <Trash2 className="mr-2 h-4 w-4" /> Retirer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="h-maths px-4 py-4">
        {occupe ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Nouvel exercice…
          </div>
        ) : (
          <>
            <MathHtmlRenderer html={exercice.enonce_html} />
            {(exercice.figure_svg_enonce || exercice.figure_svg) && (
              <div
                className="mt-2 overflow-x-auto"
                dangerouslySetInnerHTML={{
                  __html: exercice.figure_svg_enonce || exercice.figure_svg,
                }}
              />
            )}
          </>
        )}
      </div>

      {corrigeOuvert && !occupe && (
        <div className="h-maths border-t border-border bg-accent/40 px-4 py-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-accent-foreground">
            Corrigé
          </div>
          <MathHtmlRenderer html={exercice.solution_html} />
          {exercice.figure_svg_solution && (
            <div
              className="mt-2 overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: exercice.figure_svg_solution }}
            />
          )}
        </div>
      )}

      <footer className="flex flex-wrap gap-2 border-t border-border px-3 py-2">
        <Button variant="outline" size="sm" disabled={occupe} onClick={() => onRenouveler(index)}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Autres nombres
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setCorrigeOuvert((v) => !v)}>
          {corrigeOuvert ? "Masquer le corrigé" : "Voir le corrigé"}
        </Button>
      </footer>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* 3 · APRÈS LE TÉLÉCHARGEMENT — l'autre document, puis le compte      */
/* ------------------------------------------------------------------ */

function ApresTelechargement({
  ouvert, avecCorrige, onFermer, onAutreDocument, identifie, dejaConservee,
  onConserver, enregistrement,
}) {
  return (
    <Dialog open={ouvert} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="h-dialog sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Votre fiche est téléchargée</DialogTitle>
          <DialogDescription>
            {avecCorrige
              ? "Le corrigé est dans vos téléchargements."
              : "Le sujet est dans vos téléchargements."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Button variant="outline" className="w-full" onClick={onAutreDocument}>
            <Download className="mr-2 h-4 w-4" />
            Télécharger {avecCorrige ? "le sujet" : "le corrigé"}
          </Button>
          {/* Le compte est proposé APRÈS la première valeur, jamais avant —
              et le clic CONSERVE réellement cette fiche : l'intention est
              rangée avant l'inscription et rejouée au retour. Promettre de
              retrouver son travail sans l'enregistrer serait pire que de ne
              rien promettre. */}
          {!dejaConservee && (
            <div className="rounded-md border border-border bg-accent/40 p-3">
              <p className="text-sm font-medium">Garder cette fiche ?</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {identifie
                  ? "Elle rejoindra « Mes fiches », où vous pourrez la rouvrir."
                  : "Un compte gratuit vous permet de la retrouver, de la rouvrir et d'en préparer d'autres."}
              </p>
              <Button
                size="sm" className="mt-3 w-full"
                disabled={enregistrement}
                onClick={onConserver}
              >
                {enregistrement ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {identifie ? "Conserver dans Mes fiches" : "Créer un compte gratuit"}
              </Button>
            </div>
          )}
          {dejaConservee && (
            <p className="rounded-md border border-border bg-accent/40 p-3 text-xs text-muted-foreground">
              Cette fiche est conservée dans « Mes fiches ».
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* LA PAGE                                                             */
/* ------------------------------------------------------------------ */

export default function FichePageH({ accueil = false }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  // `useAuth` n'expose pas d'`isAuthenticated` : lire une clé absente donnait
  // `undefined`, donc « pas connecté » pour tout le monde — et la proposition
  // de compte s'affichait même à un utilisateur déjà identifié.
  const { sessionToken, peutSignaler } = useAuth();
  const [exerciceSignale, setExerciceSignale] = useState(null);
  const [choixOuvert, setChoixOuvert] = useState(accueil);
  const [recente, setRecente] = useState(null);
  const [repriseEnCours, setRepriseEnCours] = useState(false);
  const identifie = !!sessionToken;
  const [parametres] = useSearchParams();
  const {
    fiche, exercices, questionsExhaustives, brouillonRestaure,
    enCours, positionOccupee, erreur, etatEnregistrement, parcoursFerme,
    dernierRetrait, composer, renouveler, allonger, deplacer, retirer,
    annulerRetrait, renommer, abandonner, enregistrer, rouvrir, effacerErreur, retirerObjectif,
    erreurEnregistrement, enregistrementEnCours, stockageLocalOK,
  } = useFicheH();

  const [apercuOuvert, setApercuOuvert] = useState(false);
  const [renommage, setRenommage] = useState(null);
  const [exportEnCours, setExportEnCours] = useState(false);
  const [finOuverte, setFinOuverte] = useState(false);
  const [dernierCorrige, setDernierCorrige] = useState(false);
  const [quota, setQuota] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [abandonAConfirmer, setAbandonAConfirmer] = useState(false);
  const [repriseAConfirmer, setRepriseAConfirmer] = useState(null);
  const { openRegister } = useLogin();

  const nouvelleTraitee = useRef(false);
  useEffect(() => {
    if (!brouillonRestaure || parametres.get("nouvelle") !== "1" || nouvelleTraitee.current) return;
    nouvelleTraitee.current = true;
    const refus = abandonner();
    if (refus?.refuse) setAbandonAConfirmer(true);
    else setChoixOuvert(true);
    navigate("/fiche", { replace: true });
  }, [brouillonRestaure, parametres, abandonner, navigate]);

  // Premier enregistrement explicite ; ensuite autosave, sans toast ni boucle
  // de réessais. Une empreinte nouvelle réarme la sauvegarde. Un conflit ne
  // relit jamais une révision pour écraser ce que l'autre appareil a écrit.
  useEffect(() => {
    if (!sessionToken || !fiche?.etat?.sheet_uid || etatEnregistrement !== "modifiee" || repriseAConfirmer
      || enCours || positionOccupee || !brouillonRestaure) return undefined;
    const timer = setTimeout(() => { enregistrer(sessionToken); }, 900);
    return () => clearTimeout(timer);
  }, [sessionToken, fiche?.etat?.sheet_uid, fiche?.version_figee?.empreinte,
    etatEnregistrement, enCours, positionOccupee, brouillonRestaure, enregistrer, repriseAConfirmer]);

  // Une fiche du compte est proposée, jamais chargée à la place du brouillon.
  // Aucun document privé n'est demandé avant que la session soit connue.
  useEffect(() => {
    if (!accueil || !sessionToken || fiche) return undefined;
    let vivant = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user/sheets?limite=1`, { headers: entetesFiche() })
      .then(({ data }) => { if (vivant && Array.isArray(data)) setRecente(data[0] || null); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [accueil, sessionToken, fiche]);

  // Le brouillon a déjà été persisté par le hook avant cet effet. La fiche
  // reçoit son URL : le retour du navigateur ramène naturellement aux choix.
  useEffect(() => {
    if (accueil && fiche && !choixOuvert) navigate("/fiche");
  }, [accueil, fiche, choixOuvert, navigate]);

  /**
   * Conserver la fiche dans le compte.
   *
   * Sans compte, on ouvre l'inscription en RANGEANT l'intention : elle survit
   * au lien magique, qui remonte toute l'application. Sans cela, l'utilisateur
   * s'inscrit, revient… et sa fiche n'est nulle part — ce qui rendrait la
   * promesse « créez un compte pour la retrouver » littéralement fausse.
   */
  const conserver = useCallback(async (jetonFourni = null) => {
    const token = jetonFourni || sessionToken;
    if (!token) {
      noterActionApresConnexion("conserver_fiche_h");
      openRegister({ returnTo: "/fiche", pendingAction: { type: "conserver_fiche_h" } });
      return null;
    }
    setEnregistrement(true);
    try {
      const uid = await enregistrer(token);
      if (uid) {
        setFinOuverte(false);
        toast({
          title: "Fiche conservée",
          description: "Vous la retrouverez dans « Mes fiches ».",
        });
      }
      // L'IDENTIFIANT REMONTE À L'APPELANT. Sans lui, « Conserver puis
      // changer » ne pourrait pas savoir si la conservation a abouti — et
      // quitterait la fiche après un échec.
      return uid || null;
    } finally {
      setEnregistrement(false);
    }
  }, [sessionToken, enregistrer, openRegister, toast]);

  /* La reprise après connexion — DEUX CHEMINS, UNE SEULE INTENTION.
   *
   * La modale réussit sans quitter la page et diffuse un évènement ; le lien
   * magique, lui, recharge tout et ne laisse que la trace en session. Les deux
   * peuvent s'armer pour la même connexion : l'intention est à usage unique,
   * celui qui la consomme enregistre, l'autre ne trouve rien et se tait. */
  /* L'INTENTION NE SE CONSOMME QU'UNE FOIS LE BROUILLON RELU.
   *
   * Les deux effets ci-dessous lisaient l'intention — donc la RETIRAIENT — dès
   * que le jeton apparaissait. Or le brouillon est relu depuis `localStorage`
   * par un effet de montage de `useFicheH` : au retour d'une inscription par
   * lien magique, toute l'application se remonte, et rien ne garantit l'ordre.
   * Quand l'intention partait la première, `conserver()` sortait par sa garde
   * « pas de fiche », et l'intention n'existait plus pour être rejouée.
   * L'utilisateur s'était inscrit pour retrouver sa fiche : elle n'était nulle
   * part.
   *
   * `brouillonRestaure` dit « la lecture locale est finie », qu'un brouillon
   * ait été trouvé ou non. Les deux chemins l'attendent. L'intention reste à
   * usage unique : celui qui la consomme enregistre, l'autre se tait. */
  const enAttenteDeReprise = useRef(false);

  useEffect(() => {
    const surEvenement = (evenement) => {
      if (evenement.detail?.type !== "conserver_fiche_h") return;
      enAttenteDeReprise.current = true;
    };
    window.addEventListener("lmm:execute-pending-action", surEvenement);
    return () => window.removeEventListener("lmm:execute-pending-action", surEvenement);
  }, []);

  useEffect(() => {
    if (!brouillonRestaure) return;              // on n'y touche pas avant
    if (!sessionToken) return;
    if (!fiche) return;                          // rien à conserver, on garde l'intention
    const attendu = enAttenteDeReprise.current
      || sessionStorage.getItem("lemaitremot_action_apres_connexion") === "conserver_fiche_h";
    if (!attendu) return;
    if (reprendreActionApresConnexion() !== "conserver_fiche_h") {
      // L'évènement est arrivé sans trace en session (connexion par mot de
      // passe, sans rechargement) : l'intention reste valable.
      if (!enAttenteDeReprise.current) return;
    }
    enAttenteDeReprise.current = false;
    conserver(sessionToken);
  }, [brouillonRestaure, sessionToken, fiche, conserver]);

  /* LE RETOUR DU NAVIGATEUR FERME L'APERÇU — il ne quitte pas la fiche.
   *
   * Constaté sur la maquette, et reproduit ici avant correction : ouvrir
   * l'aperçu puis appuyer sur Retour changeait la route SOUS le panneau, qui
   * restait affiché. L'utilisateur se retrouvait sur une page qu'il ne voyait
   * pas. Sur téléphone, Retour est le geste naturel pour refermer un panneau :
   * on lui donne une entrée d'historique à consommer. */
  useEffect(() => {
    if (!apercuOuvert) return undefined;
    window.history.pushState({ lmmApercu: true }, "");
    const fermer = () => setApercuOuvert(false);
    window.addEventListener("popstate", fermer);
    return () => {
      window.removeEventListener("popstate", fermer);
      // Refermé par le bouton « Retour » de l'aperçu : on retire l'entrée
      // qu'on avait ajoutée, sinon elle resterait à consommer plus tard.
      if (window.history.state?.lmmApercu) window.history.back();
    };
  }, [apercuOuvert]);

  useEffect(() => {
    if (erreur) {
      toast({ title: erreur, variant: "destructive" });
      effacerErreur();
    }
  }, [erreur, toast, effacerErreur]);

  // Ce qui sera possible, DIT AVANT le clic.
  const rafraichirQuota = useCallback(() => {
    axios.get(`${API_FICHES}/quota`, { headers: entetesFiche() })
      .then(({ data }) => setQuota(data))
      .catch(() => setQuota(null));
  }, []);
  useEffect(() => { rafraichirQuota(); }, [rafraichirQuota, sessionToken]);

  // Accès direct à un objectif : ?objectif=<id>
  useEffect(() => {
    const vise = parametres.get("objectif");
    if (vise && !fiche && !enCours) composer(vise);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametres]);

  /* ROUVRIR UNE FICHE DU COMPTE : ?fiche=<sheet_uid>
   *
   * « Mes fiches » envoie ici les fiches composées ici. On attend que le
   * brouillon local soit relu : sans cela, la restauration du brouillon
   * écraserait la fiche qu'on vient d'aller chercher.
   *
   * ET ON VA TOUJOURS CHERCHER LE SERVEUR.
   *
   * Cet effet sortait dès que le brouillon portait le même identifiant, pour
   * s'épargner une lecture. Conséquence mesurée : après « Cette fiche a été
   * modifiée ailleurs. Rouvrez-la depuis Mes fiches », l'utilisateur clique et
   * retrouve exactement sa version périmée — le seul geste proposé pour sortir
   * du conflit ne faisait rien. Une demande explicite l'emporte sur un
   * brouillon, toujours.
   *
   * `rouverte` empêche seulement de recommencer à chaque rendu ; `rouvrir`
   * refuse de lui-même d'écraser un travail non enregistré. */
  const rouverte = useRef(null);
  useEffect(() => {
    if (!brouillonRestaure) return;
    const demandee = parametres.get("fiche");
    if (!demandee || rouverte.current === demandee) return;
    rouverte.current = demandee;
    rouvrir(demandee).then((resultat) => {
      if (resultat?.refuse === "TRAVAIL_NON_ENREGISTRE") setRepriseAConfirmer(demandee);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brouillonRestaure, parametres]);

  const telecharger = useCallback(async (avecCorrige) => {
    if (!fiche || !exercices.length) return;
    setExportEnCours(true);
    try {
      const reponse = await axios.post(
        `${API_FICHES}/export-pdf`,
        corpsExport(fiche, exercices, avecCorrige),
        { headers: entetesFiche(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(
        new Blob([reponse.data], { type: "application/pdf" })
      );
      const lien = document.createElement("a");
      lien.href = url;
      lien.download = `${fiche.titre || "fiche"}${avecCorrige ? "_corrige" : "_sujet"}.pdf`;
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      window.URL.revokeObjectURL(url);
      setDernierCorrige(avecCorrige);
      setFinOuverte(true);
      rafraichirQuota();
    } catch (e) {
      // Le corps d'une réponse d'erreur arrive en blob : il faut le relire.
      let detail = null;
      try {
        detail = JSON.parse(await e.response?.data?.text?.() || "{}")?.detail;
      } catch (_) { /* réponse non JSON */ }
      const code = detail?.code;
      toast({
        title:
          code === "GUEST_DAILY_EXPORT_LIMIT" || code === "FREE_DAILY_EXPORT_LIMIT"
            ? "Limite du jour atteinte"
            : "Le PDF n'a pas pu être créé",
        description: detail?.message || "Réessayez dans un instant.",
        variant: "destructive",
      });
      rafraichirQuota();
    } finally {
      setExportEnCours(false);
    }
  }, [fiche, exercices, toast, rafraichirQuota]);

  // UN HOOK NE PEUT PAS VIVRE APRÈS UN RETOUR CONDITIONNEL : placé sous le
  // `if (!fiche)` ci-dessous, ce `useMemo` changeait l'ordre des hooks entre
  // deux rendus et faisait tomber la page sur son écran d'erreur.
  /* La fiche est-elle encore celle que le produit a composée ?
   *
   * Comparer les LONGUEURS ne suffit pas : ajouter puis retirer ramène au même
   * compte sans ramener au même document, et la phrase de recette redevenait
   * vraie alors qu'elle ne l'était plus. On compare donc les exercices
   * eux-mêmes, dans l'ordre. */
  const estLaCompositionDOrigine = useMemo(() => {
    const attendu = fiche?.ordre || [];
    const actuel = exercices.map((ex) => ex.id_exercice);
    return attendu.length === actuel.length
      && attendu.every((uid, i) => uid === actuel[i]);
  }, [fiche, exercices]);

  const refaire = async () => {
    setRepriseEnCours(true);
    try {
      let source = fiche;
      let formes = exercices.map(ex => ({ objectif_id: ex.metadata?.objectif_id || fiche.objectif_id, libelle: ex.forme_libelle, eviter_html: ex.enonce_html }));
      if (!source && recente?.parcours === "H") {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/user/sheets/${encodeURIComponent(recente.sheet_uid)}?complet=1`, { headers: entetesFiche() });
        source = { titre: data.title, objectif_id: data.objectif_id };
        formes = (data.exercises || []).map(ex => ({ objectif_id: ex.metadata?.objectif_id || data.objectif_id, libelle: ex.metadata?.forme, eviter_html: ex.enonce_html }));
      }
      if (!source || !formes.length) return;
      const data = await composer([...new Set(formes.map(f => f.objectif_id))], { titre: source.titre, formesReprises: formes });
      if (data) setChoixOuvert(false);
    } catch (_) {
      toast({ title: "Cette fiche n'a pas pu être reprise. Votre travail est inchangé.", variant: "destructive" });
    } finally { setRepriseEnCours(false); }
  };

  if (!fiche || choixOuvert) {
    // Le service a refermé le parcours pendant qu'on y était, ou un lien
    // direct `?objectif=` a buté dessus : on affiche la fermeture plutôt que
    // de laisser le choix d'objectifs proposer ce qui ne répondra pas.
    return (
      <>{erreur && <p role="alert" className="mx-auto max-w-4xl px-5 pt-4 text-destructive">{erreur}</p>}<ChoixObjectif
        enCours={enCours || repriseEnCours}
        ferme={parcoursFerme}
        fiche={fiche || (recente ? { titre: recente.title, niveau: "dans votre compte" } : null)}
        ajout={!!fiche}
        nombreActuel={fiche ? exercices.length : 0}
        onNouvelle={fiche ? () => { const refus = abandonner(); if (refus?.refuse) { setChoixOuvert(false); setAbandonAConfirmer(true); } } : null}
        onReprendre={() => {
          if (fiche) setChoixOuvert(false);
          else if (recente) navigate(recente.parcours === "H" ? `/fiche?fiche=${encodeURIComponent(recente.sheet_uid)}` : `/mes-fiches/${encodeURIComponent(recente.sheet_uid)}`);
        }}
        onRefaire={(fiche && etatEnregistrement === "a-jour") || (!fiche && recente?.parcours === "H") ? refaire : null}
        onChoisir={async (selection) => { const data = await composer(selection.map(o => o.id), { ajouter: !!fiche }); if (data) setChoixOuvert(false); }}
      /><div className="px-5 pb-28 text-center"><SignalerLaPage autorisation={!!peutSignaler} /></div></>
    );
  }

  // La charge est recalculée sur les exercices AFFICHÉS : celle que le serveur
  // a rendue décrit la composition d'origine, pas le document après retrait,
  // ajout ou remplacement.
  const resume = resumerCharge(exercices);


  return (
    <div className="h-work min-h-screen bg-background pb-24">
      {apercuOuvert && (
        <ApercuPapier
          fiche={fiche}
          exercices={exercices}
          onFermer={() => setApercuOuvert(false)}
        />
      )}

      <div className="h-work-layout mx-auto w-full max-w-6xl lg:grid lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start lg:gap-8 lg:px-6 lg:py-6">
      <main className="mx-auto w-full min-w-0 max-w-2xl px-4 py-4 lg:px-0">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-2xl font-bold leading-tight sm:text-3xl">{fiche.titre}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">{resume}</p>
          </div>
          <Button
            variant="ghost" size="sm" aria-label="Renommer la fiche"
            onClick={() => setRenommage(fiche.titre)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
        </div>

        {/* CE QUE L'UTILISATEUR PERDRAIT, DIT AVANT QU'IL LE PERDE.
            L'état suit le CONTENU du document, pas une date : revenir à ce qui
            a été enregistré redit « à jour », parce que c'est vrai. */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-3 py-2">
          <p data-testid="etat-enregistrement" role="status" className={`text-xs ${erreurEnregistrement || stockageLocalOK === false ? "text-destructive" : "text-muted-foreground"}`}>
            {enregistrementEnCours ? "Enregistrement…" : stockageLocalOK === false && erreurEnregistrement ? "Enregistrement et stockage local indisponibles : gardez cet onglet ouvert."
              : erreurEnregistrement === "conflit" ? "Modifiée ailleurs — votre version locale est conservée."
              : erreurEnregistrement ? "Échec de l'enregistrement — version locale conservée."
              : etatEnregistrement === "a-jour" ? "Enregistrée"
              : etatEnregistrement === "modifiee" ? "Modifications non enregistrées…"
              : stockageLocalOK === false ? "Stockage local indisponible : enregistrez ou téléchargez votre fiche."
              : "Brouillon conservé sur cet appareil"}
          </p>
          {etatEnregistrement !== "a-jour" && <Button variant="outline" size="sm" disabled={enregistrement || enregistrementEnCours} onClick={() => erreurEnregistrement === "conflit" ? enregistrer(sessionToken, { copie: true }) : conserver()}>
            <BookmarkPlus className="mr-1.5 h-4 w-4" />{erreurEnregistrement === "conflit" ? "Enregistrer une copie" : erreurEnregistrement ? "Réessayer" : "Enregistrer"}
          </Button>}
          {erreurEnregistrement === "conflit" && <Button variant="ghost" size="sm" onClick={() => setRepriseAConfirmer(fiche.etat.sheet_uid)}>Voir la version du compte</Button>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setChoixOuvert(true)}>+ Ajouter un objectif</Button>
          <Button variant="ghost" disabled={enregistrementEnCours} onClick={() => { const refus = abandonner(); if (refus?.refuse) setAbandonAConfirmer(true); else setChoixOuvert(true); }}>Nouvelle fiche</Button>
        </div>
        {!!fiche.objectifs?.length && <details className="mt-3 rounded-lg border px-3">
          <summary className="flex min-h-11 cursor-pointer items-center text-sm">{fiche.objectifs.length} objectif{fiche.objectifs.length > 1 ? "s" : ""} · Gérer le contenu</summary>
          {fiche.objectifs.map(o => <div key={o.objectif_id} className="border-t py-3">
            <p className="text-sm font-medium">{o.objectif_libelle}</p>
            <div className="mt-1 flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" disabled={!!positionOccupee || exercices.length >= 12} onClick={() => allonger(o.objectif_id)}>+ Un exercice</Button>
              <Button variant="ghost" size="sm" disabled={fiche.objectifs.length === 1} onClick={() => retirerObjectif(o.objectif_id)}>Retirer cet objectif</Button>
            </div>
          </div>)}
        </details>}

        {/* Ce que le produit a décidé, dit en une phrase. Le moteur assume la
            composition ; il la rend lisible plutôt que de la laisser deviner. */}
        {/* Ce que le produit a décidé, dit en une phrase — et la phrase vient
            de la RECETTE. La figer ici la rendrait fausse dès le deuxième
            objectif. Elle ne s'affiche plus une fois la fiche ajustée : ce
            n'est alors plus la composition que le produit a choisie. */}
        {estLaCompositionDOrigine && fiche.objectifs?.length <= 1 && <p className="mt-3 text-sm text-muted-foreground">{fiche.recette_justification}</p>}

        <div className="mt-4 space-y-3">
          {exercices.map((ex, i) => (
            <ExerciceBloc
              key={ex.id_exercice || i}
              exercice={ex}
              index={i}
              total={exercices.length}
              formes={(fiche.objectifs || []).find(o => o.objectif_id === ex.metadata?.objectif_id)?.formes_autorisees || fiche.formes_autorisees || []}
              occupe={positionOccupee === ex.id_exercice}
              onRenouveler={renouveler}
              onRemplacer={(idx, libelle) => renouveler(idx, libelle)}
              onMonter={(idx) => deplacer(idx, idx - 1)}
              onDescendre={(idx) => deplacer(idx, idx + 1)}
              onRetirer={retirer}
              onSignaler={peutSignaler ? setExerciceSignale : undefined}
            />
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => allonger()} disabled={positionOccupee !== null || exercices.length >= 12}>
            + Un exercice de plus
          </Button>
          {dernierRetrait && (
            <Button variant="ghost" size="sm" onClick={annulerRetrait}>
              <Undo2 className="mr-1.5 h-4 w-4" /> Annuler le retrait
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setChoixOuvert(true)}>+ Ajouter un objectif</Button>
        </div>

        {quota && !quota.illimite && (
          <p className="mt-4 text-xs text-muted-foreground">
            {quota.restant > 0
              ? `${quota.restant} fiche${quota.restant > 1 ? "s" : ""} à télécharger aujourd'hui${quota.identifie ? "" : " sans compte"}.`
              : "Vous avez téléchargé votre fiche du jour."}
          </p>
        )}
      </main>

      {/* Barre d'action basse : sur téléphone, le téléchargement est atteignable
          sans remonter en haut de page. */}
      <aside className="h-download-dock fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-3 py-2 backdrop-blur lg:sticky lg:top-24 lg:z-auto lg:mt-16 lg:rounded-xl lg:border lg:p-5" aria-label="Votre document">
        <div className="mb-5 hidden lg:block">
          <FileText className="mb-3 h-6 w-6 text-primary" />
          <h2 className="font-serif text-xl font-semibold">Votre fiche est prête</h2>
          <p className="mt-2 text-sm text-muted-foreground">{resume}</p>
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">Gardez-la telle quelle ou ajustez les exercices. L'aperçu papier est facultatif.</p>
        </div>
        <div className="mx-auto flex w-full max-w-2xl items-center gap-2 lg:flex-col lg:items-stretch">
          <Button
            variant="outline" size="sm" className="shrink-0"
            onClick={() => setApercuOuvert(true)}
          >
            <Eye className="mr-1.5 h-4 w-4" /> Aperçu
          </Button>
          <Button
            className="flex-1" disabled={exportEnCours || !exercices.length}
            onClick={() => telecharger(false)}
          >
            {exportEnCours
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <FileText className="mr-2 h-4 w-4" />}
            PDF sujet
          </Button>
          <Button
            variant="secondary" size="sm" className="shrink-0"
            disabled={exportEnCours || !exercices.length}
            onClick={() => telecharger(true)}
          >
            Corrigé
          </Button>
        </div>
      </aside>
      </div>

      <Dialog open={renommage !== null} onOpenChange={(o) => !o && setRenommage(null)}>
        <DialogContent className="h-dialog sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Renommer la fiche</DialogTitle>
            <DialogDescription>
              Ce nom est aussi celui du document imprimé.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={renommage ?? ""}
            onChange={(e) => setRenommage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { renommer(renommage); setRenommage(null); }
            }}
            autoFocus
          />
          <Button onClick={() => { renommer(renommage); setRenommage(null); }}>
            <Check className="mr-2 h-4 w-4" /> Enregistrer le nom
          </Button>
        </DialogContent>
      </Dialog>

      {/* REPRENDRE LA VERSION DU COMPTE ÉCRASE LE TRAVAIL EN COURS.
          On le dit, et on laisse le choix — la demande explicite reste
          prioritaire, mais elle ne détruit rien en silence. */}
      <Dialog open={repriseAConfirmer !== null}
              onOpenChange={(o) => !o && setRepriseAConfirmer(null)}>
        <DialogContent className="h-dialog sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reprendre la version enregistrée ?</DialogTitle>
            <DialogDescription>
              Votre travail en cours « {fiche.titre} » comporte des modifications
              non enregistrées. Ouvrir le document demandé le remplacera à l'écran.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row-reverse">
            <Button
              className="sm:flex-1" disabled={enregistrement}
              onClick={async () => {
                const ok = erreurEnregistrement === "conflit" ? await enregistrer(sessionToken, { copie: true }) : await conserver();
                if (!ok) return;                 // échec : on ne perd rien
                const cible = repriseAConfirmer;
                setRepriseAConfirmer(null);
                rouvrir(cible, { confirme: true });
              }}
            >
              <BookmarkPlus className="mr-2 h-4 w-4" /> Enregistrer puis reprendre
            </Button>
            <Button
              variant="outline" className="sm:flex-1"
              onClick={() => {
                const cible = repriseAConfirmer;
                setRepriseAConfirmer(null);
                rouvrir(cible, { confirme: true });
              }}
            >
              Reprendre sans enregistrer
            </Button>
            <Button
              variant="ghost" className="sm:flex-1"
              onClick={() => setRepriseAConfirmer(null)}
            >
              Garder mon travail
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CHANGER D'OBJECTIF EFFACE LA FICHE. On le dit, et on propose la
          seule chose qui évite la perte : la conserver d'abord. */}
      <Dialog open={abandonAConfirmer} onOpenChange={(o) => !o && setAbandonAConfirmer(false)}>
        <DialogContent className="h-dialog sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cette fiche n'est pas enregistrée</DialogTitle>
            <DialogDescription>
              {etatEnregistrement === "modifiee"
                ? "Vos modifications depuis le dernier enregistrement seront perdues."
                : "Une nouvelle fiche remplacera ce brouillon sur cet appareil. Enregistrez-le pour le retrouver."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 sm:flex-row-reverse">
            <Button
              className="sm:flex-1"
              disabled={enregistrement}
              onClick={async () => {
                const ok = await conserver();
                setAbandonAConfirmer(false);
                // On ne quitte que si la conservation a RÉELLEMENT abouti :
                // sortir après un échec perdrait ce qu'on venait de sauver.
                if (ok) abandonner({ confirme: true });
              }}
            >
              <BookmarkPlus className="mr-2 h-4 w-4" />
              {identifie ? "Enregistrer puis créer une nouvelle fiche" : "Créer un compte pour la garder"}
            </Button>
            <Button
              variant="outline" className="sm:flex-1"
              onClick={() => { setAbandonAConfirmer(false); abandonner({ confirme: true }); }}
            >
              Nouvelle fiche sans enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mx-auto max-w-2xl px-4 py-6"><SignalerLaPage autorisation={!!peutSignaler} contexte={{ niveau: fiche.niveau, chapitre: fiche.code_officiel, chapitre_libelle: fiche.objectif_libelle }} /></div>
      <BugReportDialog open={!!exerciceSignale} onOpenChange={(ouvert) => { if (!ouvert) setExerciceSignale(null); }}
        exercise={exerciceSignale} chapterCode={exerciceSignale?.metadata?.code_officiel || fiche.code_officiel} objectif={{ id: exerciceSignale?.metadata?.objectif_id || fiche.objectif_id, libelle: exerciceSignale?.metadata?.objectif_libelle || fiche.objectif_libelle, recette_id: exerciceSignale?.metadata?.recette_id || fiche.recette_id, recette_version: exerciceSignale?.metadata?.recette_version || fiche.recette_version }} />
      <ApresTelechargement
        ouvert={finOuverte}
        avecCorrige={dernierCorrige}
        identifie={identifie}
        dejaConservee={!!fiche.etat?.sheet_uid}
        enregistrement={enregistrement}
        onConserver={() => conserver()}
        onFermer={() => setFinOuverte(false)}
        onAutreDocument={() => { setFinOuverte(false); telecharger(!dernierCorrige); }}
      />
    </div>
  );
}
